import React from 'react';
import { User } from 'firebase/auth';
import { Library } from 'lucide-react';
import { TaskBankIdeasButtons } from './task-bank-ideas-buttons';
import { ScoreButton } from './score-button';
import { EssayPlan } from './essay-plan';
import { Task } from '@/app/task-bank/public-actions';
import {
  StructuredEssayData,
  StructuredWritingData,
  EssayPlanItem,
  WritingTaskType,
  BodyParagraphType,
} from '@/types/writing';
import { QuestionType, QuestionNote } from '@/types/task-bank';

interface EssayEditorSidebarProps {
  hasSelectedTask: boolean;
  selectedTask: Task | null;
  user: User | null;
  userProfile: { role?: string } | null | undefined;
  prompt: string;
  showIdeas: boolean;
  setShowIdeas: (show: boolean) => void;
  setTaskBankOpen: (open: boolean) => void;
  isScoring: boolean;
  essayData: StructuredEssayData;
  currentEssayId: string | null;
  fetchNewScore: (
    prompt: string,
    essayData: StructuredEssayData | StructuredWritingData,
    taskType?: WritingTaskType,
    questionType?: QuestionType | '' | undefined,
    questionNote?: QuestionNote | '' | undefined
  ) => void;
  canScore: boolean;
  requirements: string[];
  essayPlan: EssayPlanItem[];
  handlePlanTypeChange: (
    idx: number,
    type: BodyParagraphType | undefined
  ) => void;
  handlePlanContentChange: (idx: number, content: string) => void;
  addPlanItem: () => void;
  removePlanItem: (idx: number) => void;
  handleKeyDown: (event: React.KeyboardEvent<HTMLTextAreaElement>) => void;
  isPlanOpen: boolean;
  onPlanOpenChange: (open: boolean) => void;
  currentQuestionType?: QuestionType | '' | undefined;
  currentQuestionNote?: QuestionNote | '' | undefined;
}

export function EssayEditorSidebar({
  hasSelectedTask,
  selectedTask,
  user,
  userProfile,
  prompt,
  showIdeas,
  setShowIdeas,
  setTaskBankOpen,
  isScoring,
  essayData,
  currentEssayId,
  fetchNewScore,
  canScore,
  requirements,
  essayPlan,
  handlePlanTypeChange,
  handlePlanContentChange,
  addPlanItem,
  removePlanItem,
  handleKeyDown,
  isPlanOpen,
  onPlanOpenChange,
  currentQuestionType,
  currentQuestionNote,
}: EssayEditorSidebarProps) {
  return (
    <>
      <div className='flex flex-col sm:flex-row gap-2 flex-shrink-0'>
        <TaskBankIdeasButtons
          user={user}
          selectedTask={selectedTask}
          prompt={prompt}
          showIdeas={showIdeas}
          setShowIdeas={setShowIdeas}
          setTaskBankOpen={setTaskBankOpen}
        />
        {selectedTask && (
          <ScoreButton
            isProUser={
              userProfile?.role === 'pro' || userProfile?.role === 'admin'
            }
            isScoring={isScoring}
            prompt={prompt}
            essayData={essayData}
            currentEssayId={currentEssayId}
            user={user}
            fetchNewScore={fetchNewScore}
            questionType={currentQuestionType}
            questionNote={currentQuestionNote}
            canScore={canScore}
            requirements={requirements}
          />
        )}
      </div>
      <div className='flex flex-col space-y-2 flex-shrink-0'>
        {hasSelectedTask ? (
          <>
            <div className='w-full border rounded-lg bg-muted p-4 font-bold text-base text-foreground min-h-[80px] whitespace-pre-line'>
              {selectedTask?.prompt}
            </div>

            <EssayPlan
              plan={essayPlan}
              handlePlanTypeChange={handlePlanTypeChange}
              handlePlanContentChange={handlePlanContentChange}
              addPlanItem={addPlanItem}
              removePlanItem={removePlanItem}
              handleKeyDown={handleKeyDown}
              isPlanOpen={isPlanOpen}
              onPlanOpenChange={onPlanOpenChange}
            />
          </>
        ) : (
          <div
            className='flex flex-col items-center justify-center py-12 border-2 border-dashed border-muted rounded-lg bg-muted/40 cursor-pointer transition hover:bg-muted/60'
            onClick={() => setTaskBankOpen(true)}
            role='button'
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') setTaskBankOpen(true);
            }}
          >
            <Library className='h-8 w-8 text-muted-foreground mb-2' />
            <span className='text-lg text-muted-foreground font-semibold'>
              Select a task to begin
            </span>
          </div>
        )}
      </div>
    </>
  );
}
